import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Hook to fetch dynamic routes from module manifests.
 * Deferred until idle after login so it does not block shell paint.
 */
export const useDynamicRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRoutes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/api/module-registry/routes`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRoutes(response.data.routes || []);
    } catch (err) {
      console.error('Failed to fetch dynamic routes:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let idleId;
    let timeoutId;

    const run = () => {
      if (!cancelled) fetchRoutes();
    };

    // Defer until browser is idle (or 1.5s fallback) so session/shell paint first
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(run, 1500);
    }

    const handleLogin = () => {
      setLoading(true);
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(run, { timeout: 2000 });
      } else {
        timeoutId = setTimeout(run, 500);
      }
    };

    window.addEventListener('user-login', handleLogin);
    return () => {
      cancelled = true;
      window.removeEventListener('user-login', handleLogin);
      if (idleId != null && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const refresh = () => {
    setLoading(true);
    fetchRoutes();
  };

  return { routes, loading, error, refresh };
};
